import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { ParseRequestCreatePage } from '../parse-request-create-page/parse-request-create-page';
import { ParseRequestCreateGeneralFormModel, toModel, toPayload } from './definitions';
import { disabled, form, FormField, required } from '@angular/forms/signals';
import {
  Button,
  Alert,
  InputLabel,
  RadioGroup,
  Radio,
  InputSimple,
  InputError,
} from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { ParseRequestsStore } from '../../stores/parse-requests.store';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ParseRequestCreateAccordion } from '../../components/parse-request-create-accordion/parse-request-create-accordion';

@Component({
  selector: 'app-parse-request-create-general-page',
  imports: [
    Button,
    NgIcon,
    Alert,
    InputLabel,
    RadioGroup,
    Radio,
    FormField,
    FieldErrors,
    InputSimple,
    InputError,
    TranslocoPipe,
    ParseRequestCreateAccordion,
    RouterLink,
  ],
  templateUrl: './parse-request-create-general-page.html',
})
export class ParseRequestCreateGeneralPage {
  private router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  private parseRequestsStore = inject(ParseRequestsStore);
  private parent = inject(ParseRequestCreatePage);

  protected state = this.parent.state;

  protected isUpdateLoading = computed(() => !!this.state()?.isUpdateLoading);
  protected updateError = computed(() => this.state()?.updateError ?? null);
  protected showValidationErrors = signal(false);

  private model = linkedSignal<ParseRequestCreateGeneralFormModel>(() => {
    const parseRequest = this.state()?.data;
    if (!parseRequest) {
      return toModel({});
    }
    return toModel(parseRequest);
  });

  form = form(this.model, (schemaPath) => {
    // businessRelation
    required(schemaPath.businessRelation);
    // businessPartnerName
    required(schemaPath.businessPartnerName);
    disabled(
      schemaPath.businessPartnerName,
      (ctx) => ctx.valueOf(schemaPath.businessRelation) !== 'BROKER',
    );
    // satisfied
    required(schemaPath.satisfied);
    // unsatisfiedReason
    required(schemaPath.unsatisfiedReason);
    disabled(schemaPath.unsatisfiedReason, (ctx) => ctx.valueOf(schemaPath.satisfied) !== 'NO');
    // unsatisfiedOtherReason
    required(schemaPath.unsatisfiedOtherReason);
    disabled(
      schemaPath.unsatisfiedOtherReason,
      (ctx) =>
        ctx.valueOf(schemaPath.satisfied) !== 'NO' ||
        ctx.valueOf(schemaPath.unsatisfiedReason) !== 'OTHER',
    );
  });

  private markAsTouched() {
    this.form().markAsTouched();
    this.form.businessRelation().markAsTouched();
    this.form.businessPartnerName().markAsTouched();
    this.form.satisfied().markAsTouched();
    this.form.unsatisfiedReason().markAsTouched();
    this.form.unsatisfiedOtherReason().markAsTouched();
  }

  private saveData() {
    return this.parseRequestsStore.update(this.parent.parseRequestId(), {
      ...toPayload(this.form().value()),
      generalInformationState: 'COMPLETE',
      summaryState: 'IN_PROGRESS',
    });
  }

  protected async onSubmit() {
    if (!this.form().valid()) {
      this.markAsTouched();
      this.showValidationErrors.set(true);
      return;
    }
    await this.saveData();
    this.form().reset();
    this.router.navigate(['../summary'], {
      relativeTo: this.activatedRoute,
    });
  }
}
